
import { apiFetch } from './apiConfig';
import { getAuthHeaders } from './authUtils';
import axios from 'axios';

// Global utility function to get auth token
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

class TokenService {
  constructor() {
    this.refreshPromise = null;
    this.heartbeatInterval = null;
    this.activityCheckInterval = null;
    this.autoLogoutTimeout = 10; // Default 10 minutes, wird von Server geladen
    this.warningCallback = null;
    this.warningShown = false; // Verhindert mehrfache Warnung
    

    
    // NUR Interceptors setzen wenn Token-basierte Auth verwendet wird
    // (wenn Session-basierte Auth aktiv ist, soll sessionService die Interceptors handhaben)
    const hasToken = localStorage.getItem('token');
    const hasSession = sessionStorage.getItem('session_id');
    
    if (hasToken && !hasSession) {
      // Nur bei Token-basierter Auth ohne aktive Session
      this.setupAxiosInterceptors();
      this.setupWindowCloseHandler();
      this.startActivityTracking();
      this.loadAutoLogoutConfig();
    } else {
      // NICHT starten wenn Session aktiv ist!
      return;
    }
  }

  setupWindowCloseHandler() {
    // INTELLIGENTE Browser-Close Detection
    // Problem: beforeunload feuert auch bei Refresh/Reload
    // Lösung: Verwende Session-Storage Flag + Heartbeat-System
    
    let isRefreshing = false;
    
    // Erkenne Refresh/Reload
    window.addEventListener('beforeunload', (event) => {
      // Setze Flag dass Page verlassen wird (könnte Refresh oder Close sein)
      sessionStorage.setItem('pageUnloading', 'true');
      isRefreshing = true;
      
      // NICHT sofort Token löschen - könnte Refresh sein!

    });

    // Beim Page Load prüfen ob es ein Refresh war
    window.addEventListener('load', () => {
      const wasUnloading = sessionStorage.getItem('pageUnloading');
      if (wasUnloading) {
        sessionStorage.removeItem('pageUnloading');

        isRefreshing = false;
      }
    });

    // Alternative: Nur bei echtem Close (pagehide mit persisted=false)
    window.addEventListener('pagehide', (event) => {
      if (!event.persisted && !isRefreshing) {
        // Echtes Schließen (nicht Back/Forward Cache)

        this.clearSession();
      }
    });

    // Backup: Visibility Change nur bei längerem Hidden-State
    let hiddenTimeout;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Starte Timer - wenn länger als 30 Sekunden hidden, könnte Browser zu sein
        hiddenTimeout = setTimeout(() => {
          if (document.visibilityState === 'hidden') {

            this.clearSession();
          }
        }, 30000); // 30 Sekunden
      } else {
        // Page wieder sichtbar - Timer abbrechen
        if (hiddenTimeout) {
          clearTimeout(hiddenTimeout);
          hiddenTimeout = null;
        }
      }
    });
  }

  clearSession() {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    this.stopHeartbeat();
    this.stopActivityTracking();
  }

  // Lädt Auto-Logout Konfiguration vom Server
  async loadAutoLogoutConfig() {
    try {

      const token = this.getToken();
            if (!token) {
        return;
      }
      const response = await axios.get('http://localhost:25000/config/autologout');
      if (response.data.success) {
        this.autoLogoutTimeout = response.data.autologout_minutes;
      }
    } catch (error) {

    }
  }

  // Activity-Tracking starten
  startActivityTracking() {
    // Track User-Aktivität
    const trackActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
      // Reset warning flag bei Aktivität
      this.warningShown = false;
    };

    // Event Listener für User-Aktivität
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, trackActivity, true);
    });

    // Regelmäßige Prüfung auf Inaktivität (alle 30 Sekunden)
    this.activityCheckInterval = setInterval(() => {
      this.checkInactivity();
    }, 30000); // 30 Sekunden
  }

  stopActivityTracking() {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
  }

  // Prüft Inaktivität und zeigt Warnung
  checkInactivity() {
    const token = this.getToken();
    if (!token) return;

    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) return;

    const now = Date.now();
    const lastActivityTime = parseInt(lastActivity);
    const inactiveMinutes = (now - lastActivityTime) / (1000 * 60);

    console.log(`⏱️ DEBUG: checkInactivity - inactiveMinutes: ${inactiveMinutes.toFixed(2)}, autoLogoutTimeout: ${this.autoLogoutTimeout}, warningShown: ${this.warningShown}`);

    // KORRIGIERT: Dialog genau bei konfiguriertem Wert anzeigen
    // Warnung wird bei Erreichen der konfigurierten Minuten angezeigt
    // dann läuft 60 Sekunden Countdown bis zum echten Logout
    if (inactiveMinutes >= this.autoLogoutTimeout && !this.warningShown) {

      this.warningShown = true;
      this.showAutoLogoutWarning();
    } else if (inactiveMinutes >= (this.autoLogoutTimeout + 1)) {
      // Auto-Logout durchführen (1 Minute nach Warnung)

      this.performAutoLogout();
    } else if (inactiveMinutes < this.autoLogoutTimeout) {
      // Reset warning flag wenn User wieder aktiv wird
      if (this.warningShown) {

      }
      this.warningShown = false;
    }
  }

  // Auto-Logout Warning anzeigen
  showAutoLogoutWarning() {

    if (this.warningCallback && typeof this.warningCallback === 'function') {
      this.warningCallback();
    }
  }

  // Auto-Logout durchführen
  performAutoLogout() {
    this.clearSession();
    // Weiterleitung zur Login-Seite
    window.location.href = '/login';
  }

  // Callback für Warning Dialog setzen
  setWarningCallback(callback) {
    this.warningCallback = callback;
  }

  // Aktivität zurücksetzen (wenn User auf "Abbrechen" klickt)
  resetActivity() {
    localStorage.setItem('lastActivity', Date.now().toString());
    this.warningShown = false; // Warnung kann wieder angezeigt werden
  }

  setupAxiosInterceptors() {
    // Request interceptor to add token to all requests
    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // Update last activity on every request
          localStorage.setItem('lastActivity', Date.now().toString());
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const newToken = await this.refreshToken();
            if (newToken) {
              // Update the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              // Retry the original request
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // If refresh fails, redirect to login
            this.handleTokenExpired();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  async performTokenRefresh() {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      throw new Error('No token available for refresh');
    }

    try {
      const response = await axios.post('http://localhost:25000/auth/refresh', {}, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('lastActivity', Date.now().toString());
      

      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  handleTokenExpired() {
    // Clear token and redirect to login
    this.clearSession();
    
    // Only redirect if we're not already on the login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // Manual token refresh for proactive refresh
  async refreshTokenProactively() {
    try {
      await this.refreshToken();
    } catch (error) {
      console.warn('Proactive token refresh failed:', error);
      // Don't redirect on proactive refresh failure
    }
  }

  // Get current token
  getToken() {
    return localStorage.getItem('token');
  }

  // Set token
  setToken(token) {
    localStorage.setItem('token', token);
    localStorage.setItem('lastActivity', Date.now().toString());
    this.startHeartbeat();
  }

  // Remove token
  removeToken() {
    this.clearSession();
  }

  // Start periodic token refresh and heartbeat
  startPeriodicRefresh() {
    // Refresh token every 7 hours (25200000 ms)
    setInterval(() => {
      this.refreshTokenProactively();
    }, 25200000);
  }

  // Heartbeat system to keep session alive
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing heartbeat
    
    // Send heartbeat every 5 minutes to keep session active
    this.heartbeatInterval = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActivity');
      const now = Date.now();
      
      // Only send heartbeat if there was recent activity (within last 30 minutes)
      if (lastActivity && (now - parseInt(lastActivity)) < 30 * 60 * 1000) {
        this.sendHeartbeat();
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  async sendHeartbeat() {
    try {
      const token = this.getToken();
      if (!token) return;

      // Simple ping to keep session alive
      await axios.get('http://localhost:25000/auth/me', {
        headers: { ...getAuthHeaders() }
      });
      

    } catch (error) {

      // If heartbeat fails with 401, token might be expired
      if (error.response?.status === 401) {
        try {
          await this.refreshToken();

        } catch (refreshError) {
          console.error('Token refresh failed after heartbeat failure');
          this.handleTokenExpired();
        }
      }
    }
  }

  // Check if we need to refresh token soon (within 1 hour)
  async refreshTokenIfNeeded() {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      // Decode JWT to check expiry (simple version)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = exp - now;

      // If token is already expired or expires within 1 hour, refresh it
      if (timeUntilExpiry < 60 * 60 * 1000) {
        await this.refreshToken();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      try {
        await this.refreshToken();
        return true;
      } catch (refreshError) {
        console.error('Refresh after decode error failed:', refreshError);
        return false;
      }
    }
  }
}

// Create and export a singleton instance
const tokenService = new TokenService();
export default tokenService; 